import { requireAuth } from '@/auth/requireAuth';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { PageTitle } from '@/layout/page-title';
import { cn } from '@/lib/utils';
import { getPeople, postPerson } from '@/people';
import { createFileRoute, useLoaderData, useRouter } from '@tanstack/react-router'
import { CheckIcon, ChevronsUpDownIcon } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from "@hookform/resolvers/zod"
import toast from 'react-hot-toast';
import { LoadingSpinner } from '@/components/ui/spinner';
import { Card, CardTitle } from '@/components/ui/card';
export const Route = createFileRoute('/(app)/_layout/users/create')({
  component: RouteComponent,
  beforeLoad: requireAuth,
  loader: async () => {
    const { data: people, error: _ } = await getPeople();
    
    return { people };
  }
})

function RouteComponent() {

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { people } = useLoaderData({ from: '/(app)/_layout/users/create' });
  const peopleEmails = people?.map(person => person.email) || [];

  const formSchema = z.object({
    email: z.string().min(2, {
      message: "Email must be at least 2 characters.",
    }),
    name: z.string().min(2, {
      message: "Name must be at least 2 characters.",
    }),
    managerEmail: z.string().optional(),
    isAdmin: z.boolean(),
    isClevel: z.boolean(),
  })
  const router = useRouter();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      email: '',
      managerEmail: '',
      isAdmin: false,
      isClevel: false,
    },
    mode: 'onChange',

  })

  async function onSubmit(values: z.infer<typeof formSchema>) {
    // Do something with the form values.
    // ✅ This will be type-safe and validated.
    setLoading(true)
    const { error } = await postPerson({
      name: values.name,
      email: values.email,
      managerEmail: values.managerEmail ?? null,
      isAdmin: values.isAdmin,
      isClevel: values.isClevel,
    });
    setLoading(false)
    if (error) {
      toast.error("Error creating user: " + error);
      return;
    }
    toast.success("User created successfully!");
    router.navigate({ to: '/users' });
  }

  return (
    <div className="container max-w-[1900px] mx-auto py-10 px-4">
      <div className="space-y-4">
        <PageTitle title={`New User`} />

        {/* You can add more user details here */}
        <Card className="bg-white p-4 rounded shadow">
          <CardTitle className="text-xl font-semibold">User Information</CardTitle>
          <Form {...form} >
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input placeholder="name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input placeholder="email@example.com" {...field} />
                    </FormControl>
                    <FormDescription>This is your public display email.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="managerEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Manager Email</FormLabel>
                    <FormControl>
                      <Popover open={open} onOpenChange={setOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={open}
                            className="w-full h-8 justify-between overflow-hidden"
                          >
                            {field.value ? field.value
                              : "Select manager..."}
                            <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[300px] p-0">
                          <Command>
                            <CommandInput placeholder="Search company..." />
                            <CommandList>
                              <CommandEmpty>No email found.</CommandEmpty>
                              <CommandGroup>
                                {peopleEmails.map((email) => (
                                  <CommandItem
                                    key={email}
                                    value={email}
                                    onSelect={() => {
                                      field.onChange(email)
                                      setOpen(false)  // Close the popover after selection
                                    }
                                    }

                                  >
                                    <CheckIcon
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        field.value === email ? "opacity-100" : "opacity-0"
                                      )}
                                    />
                                    {email}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </FormControl>
                    <FormDescription>Select the manager's email from the list.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="isAdmin"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Is Admin</FormLabel>
                    <FormControl>
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={e => field.onChange(e.target.checked)}
                      />
                    </FormControl>
                    <FormDescription>Admin privileges for this user.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="isClevel"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Is C-Level</FormLabel>
                    <FormControl>
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={e => field.onChange(e.target.checked)}
                      />
                    </FormControl>
                    <FormDescription>C-Level executive status.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                disabled={loading}
                type="submit"
                className="mt-4"
              >
                Save Changes
                {loading && <LoadingSpinner />}
              </Button>
            </form>
          </Form>

        </Card>
      </div>
    </div>
  )
}
